/**
 * StreamsPage - CINECLUBS Premium Live Streams Discovery
 * YouTube Live + Twitch Hybrid Design
 * Dark cinematic theme with red-purple gradients
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { streamApi } from '../utils/api';
import { getSocket } from '../utils/socket';
import UniversalNavbar from '../components/UniversalNavbar';

// No demo streams - only real streams will be shown

// Sidebar categories
const SIDEBAR_CATEGORIES = [
  { id: 'home', label: 'Home', icon: 'fas fa-home', path: '/' },
  { id: 'live', label: 'Live Streams', icon: 'fas fa-broadcast-tower', path: '/live/streams', active: true },
  { id: 'watchparty', label: 'Watch Party', icon: 'fas fa-users', path: '/live/streams?category=watchparty' },
  { id: 'gaming', label: 'Gaming', icon: 'fas fa-gamepad', path: '/live/streams?category=gaming' },
  { id: 'music', label: 'Music', icon: 'fas fa-music', path: '/live/streams?category=music' },
  { id: 'art', label: 'Art', icon: 'fas fa-palette', path: '/live/streams?category=art' },
  { id: 'tech', label: 'Tech', icon: 'fas fa-laptop-code', path: '/live/streams?category=tech' },
  { id: 'cooking', label: 'Cooking', icon: 'fas fa-utensils', path: '/live/streams?category=cooking' },
  { id: 'fitness', label: 'Fitness', icon: 'fas fa-dumbbell', path: '/live/streams?category=fitness' },
  { id: 'divider', label: '', icon: '' },
  { id: 'following', label: 'Following', icon: 'fas fa-heart', path: '/live/streams?filter=following' },
];

// Filter options
const FILTER_OPTIONS = [
  { id: 'trending', label: 'Trending', icon: 'fas fa-fire' },
  { id: 'recent', label: 'Recently Started', icon: 'fas fa-clock' },
  { id: 'viewers', label: 'Most Viewers', icon: 'fas fa-eye' },
  { id: 'following', label: 'Following', icon: 'fas fa-heart' },
  { id: 'recommended', label: 'For You', icon: 'fas fa-star' },
];

// Sort options
const SORT_OPTIONS = [
  { id: 'viewers', label: 'Sort by Viewers' },
  { id: 'recent', label: 'Sort by Recent' },
  { id: 'category', label: 'Sort by Category' },
];

// ============================================
// COLLAPSIBLE SIDEBAR COMPONENT
// ============================================

const Sidebar = ({ isOpen, onToggle, selectedCategory, onCategoryChange }) => {
  const navigate = useNavigate();
  
  const handleItemClick = (item) => {
    if (item.id === 'divider') return;
    if (item.id === 'home') {
      navigate('/');
    } else {
      onCategoryChange(item.id);
      // Close sidebar on mobile after selection
      if (window.innerWidth < 1100) {
        onToggle();
      }
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={onToggle} />}
      
      <aside className={`sidebar ${isOpen ? 'open' : 'collapsed'}`}>
        <div className="sidebar-header">
          <button className="sidebar-close" onClick={onToggle}>
            <i className="fas fa-times"></i>
          </button>
          <span className="sidebar-title">Categories</span>
        </div>
        <div className="sidebar-content">
          {SIDEBAR_CATEGORIES.map((item) => (
            item.id === 'divider' ? (
              <div key={item.id} className="sidebar-divider" />
            ) : (
              <button
                key={item.id}
                className={`sidebar-item ${selectedCategory === item.id ? 'active' : ''}`}
                onClick={() => handleItemClick(item)}
                title={item.label}
              >
                <i className={item.icon}></i>
                <span className="sidebar-label">{item.label}</span>
              </button>
            )
          ))}
        </div>
      </aside>
    </>
  );
};

// ============================================
// FEATURED STREAM (Large Hero Card)
// ============================================

const FeaturedStream = ({ stream, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const formatViewers = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count || 0;
  };
  
  const formatDuration = (date) => {
    if (!date) return '';
    const now = new Date();
    const started = new Date(date);
    const diff = Math.floor((now - started) / 1000 / 60);
    if (diff < 60) return `${diff}m`;
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      className="featured-stream" 
      onClick={() => onClick(stream)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="featured-thumbnail">
        {stream.thumbnail ? (
          <img 
            src={stream.thumbnail} 
            alt={stream.title}
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
          />
        ) : (
          <div className="featured-placeholder">
            <i className="fas fa-broadcast-tower"></i>
          </div>
        )}
        
        {/* Gradient overlay */}
        <div className="featured-gradient" />
        
        {/* Top badges */}
        <div className="featured-top-badges">
          {stream.is_live && (
            <span className="live-badge-featured">
              <span className="live-pulse"></span>
              LIVE
            </span>
          )}
          {stream.started_at && (
            <span className="duration-badge">
              <i className="fas fa-clock"></i>
              {formatDuration(stream.started_at)}
            </span>
          )}
        </div>
        
        {/* Bottom info overlay */}
        <div className="featured-bottom">
          <div className="featured-info-overlay">
            <div className="featured-avatar">
              {stream.streamer?.avatar ? (
                <img src={stream.streamer.avatar} alt={stream.streamer.username} />
              ) : (
                <div className="avatar-letter">{stream.streamer?.username?.charAt(0)?.toUpperCase() || '?'}</div>
              )}
              <span className="avatar-live-ring"></span>
            </div>
            <div className="featured-text">
              <h2 className="featured-title">{stream.title}</h2>
              <div className="featured-meta">
                <span className="streamer-name">{stream.streamer?.username}</span>
                <span className="meta-dot">•</span>
                <span className="viewer-count">
                  <i className="fas fa-eye"></i>
                  {formatViewers(stream.viewers_count)} watching
                </span>
                {stream.category && (
                  <>
                    <span className="meta-dot">•</span>
                    <span className="category-tag">{stream.category}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Watch button */}
          <button className={`watch-btn ${isHovered ? 'visible' : ''}`}>
            <i className="fas fa-play"></i>
            Watch Now
          </button>
        </div>
        
        {/* Hover play overlay */}
        {isHovered && (
          <div className="featured-hover-play">
            <div className="play-circle">
              <i className="fas fa-play"></i>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// STREAM CARD COMPONENT (YouTube/Twitch Style)
// ============================================

const StreamCard = ({ stream, onClick, onFollow }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  
  const formatViewers = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count || 0;
  };
  
  const formatDuration = (date) => {
    if (!date) return '';
    const now = new Date();
    const started = new Date(date);
    const diff = Math.floor((now - started) / 1000 / 60);
    if (diff < 60) return `${diff}m`;
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    return `${hours}:${mins.toString().padStart(2, '0')}`;
  };
  
  const handleFollowClick = (e) => {
    e.stopPropagation();
    setIsFollowing(!isFollowing);
    if (onFollow) onFollow(stream, !isFollowing);
  };

  return (
    <div
      className="stream-card"
      onClick={() => onClick(stream)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Thumbnail */}
      <div className="card-thumbnail">
        {stream.thumbnail ? (
          <img 
            src={stream.thumbnail} 
            alt={stream.title} 
            loading="lazy"
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
          />
        ) : (
          <div className="thumbnail-placeholder">
            <i className="fas fa-video"></i>
          </div>
        )}
        
        {/* Live badge */}
        {stream.is_live && (
          <div className="card-live-badge">
            <span className="live-dot"></span>
            LIVE
          </div>
        )}
        
        {/* Offline badge */}
        {!stream.is_live && (
          <div className="card-offline-badge">OFFLINE</div>
        )}
        
        {/* Viewer count */}
        <div className="card-viewers">
          <i className="fas fa-user"></i>
          {formatViewers(stream.viewers_count)}
        </div>
        
        {/* Duration */}
        {stream.is_live && stream.started_at && (
          <div className="card-duration">{formatDuration(stream.started_at)}</div>
        )}
        
        {/* Hover overlay with play */}
        <div className={`card-hover-overlay ${isHovered ? 'visible' : ''}`}>
          <div className="hover-play-btn">
            <i className="fas fa-play"></i>
          </div>
        </div>
        
        {/* Follow button on hover */}
        <button 
          className={`card-follow-btn ${isHovered ? 'visible' : ''} ${isFollowing ? 'following' : ''}`}
          onClick={handleFollowClick}
        >
          <i className={`fas ${isFollowing ? 'fa-heart' : 'fa-plus'}`}></i>
        </button>
      </div>
      
      {/* Info section */}
      <div className="card-info">
        <div className="card-avatar">
          {stream.streamer?.avatar ? (
            <img src={stream.streamer.avatar} alt={stream.streamer.username} />
          ) : (
            <div className="avatar-placeholder">
              {stream.streamer?.username?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          {stream.is_live && <span className="online-dot"></span>}
        </div>
        
        <div className="card-details">
          <h3 className="card-title">{stream.title || 'Untitled Stream'}</h3>
          <p className="card-streamer">{stream.streamer?.username || 'Unknown'}</p>
          {stream.category && (
            <span className="card-category">{stream.category}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// RECOMMENDED HORIZONTAL SCROLL
// ============================================

const RecommendedSection = ({ streams, onStreamClick }) => {
  const scrollRef = useRef(null);
  
  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!streams || streams.length === 0) return null;

  return (
    <section className="recommended-section">
      <div className="section-header">
        <h2 className="section-title">
          <i className="fas fa-star"></i>
          Recommended for You
        </h2>
        <div className="scroll-buttons">
          <button className="scroll-btn" onClick={() => scroll('left')}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <button className="scroll-btn" onClick={() => scroll('right')}>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
      
      <div className="recommended-scroll" ref={scrollRef}>
        {streams.map(stream => (
          <div key={stream.id} className="recommended-card" onClick={() => onStreamClick(stream)}>
            <div className="rec-thumbnail">
              {stream.thumbnail ? (
                <img src={stream.thumbnail} alt={stream.title} />
              ) : (
                <div className="rec-placeholder"><i className="fas fa-video"></i></div>
              )}
              {stream.is_live && (
                <span className="rec-live">
                  <span className="live-dot"></span>
                  LIVE
                </span>
              )}
              <span className="rec-viewers">
                <i className="fas fa-eye"></i>
                {stream.viewers_count >= 1000 ? `${(stream.viewers_count/1000).toFixed(1)}K` : stream.viewers_count}
              </span>
            </div>
            <div className="rec-info">
              <h4>{stream.title}</h4>
              <p>{stream.streamer?.username}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// ============================================
// FILTER BAR
// ============================================

const FilterBar = ({ activeFilter, onFilterChange, sortBy, onSortChange, searchQuery, onSearchChange }) => {
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  
  return (
    <div className="filter-bar">
      <div className="filter-chips">
        {FILTER_OPTIONS.map(filter => (
          <button
            key={filter.id}
            className={`filter-chip ${activeFilter === filter.id ? 'active' : ''}`}
            onClick={() => onFilterChange(filter.id)}
          >
            <i className={filter.icon}></i>
            <span>{filter.label}</span>
          </button>
        ))}
      </div>
      
      <div className="filter-actions">
        {/* Search */}
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search streams..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => onSearchChange('')}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        
        {/* Sort dropdown */}
        <div className="sort-dropdown-wrapper">
          <button 
            className="sort-btn"
            onClick={() => setShowSortDropdown(!showSortDropdown)}
          >
            <i className="fas fa-sort"></i>
            <span>{SORT_OPTIONS.find(s => s.id === sortBy)?.label || 'Sort'}</span>
            <i className="fas fa-chevron-down"></i>
          </button>
          
          {showSortDropdown && (
            <div className="sort-dropdown">
              {SORT_OPTIONS.map(option => (
                <button
                  key={option.id}
                  className={`sort-option ${sortBy === option.id ? 'active' : ''}`}
                  onClick={() => {
                    onSortChange(option.id);
                    setShowSortDropdown(false);
                  }}
                >
                  {option.label}
                  {sortBy === option.id && <i className="fas fa-check"></i>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// LOADING SKELETON
// ============================================

const LoadingSkeleton = ({ count = 10 }) => (
  <div className="streams-grid">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="stream-card skeleton">
        <div className="card-thumbnail skeleton-thumb"></div>
        <div className="card-info">
          <div className="skeleton-avatar"></div>
          <div className="card-details">
            <div className="skeleton-title"></div>
            <div className="skeleton-name"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ============================================
// MAIN STREAMS PAGE
// ============================================

const StreamsPage = () => {
  const navigate = useNavigate();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('live');
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeFilter, setActiveFilter] = useState('trending');
  const [sortBy, setSortBy] = useState('viewers');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  // User & Modal states
  const [currentUser, setCurrentUser] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showStreamerRequestModal, setShowStreamerRequestModal] = useState(false);
  const [streamerForm, setStreamerForm] = useState({
    fullName: '',
    email: '',
    username: '',
    channelLink: '',
    reason: ''
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [cryptoLoading, setCryptoLoading] = useState(false);
  
  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch('/api/auth/me.php', {
          credentials: 'include'
        });
        const data = await response.json();
        if (data.status === 'success' && (data.user || data.data)) {
          setCurrentUser(data.user || data.data);
        }
      } catch (err) {
        console.error('Failed to fetch user:', err);
      }
    };
    fetchUser();
  }, []);
  
  // Handle Go Live click
  const handleGoLiveClick = () => {
    // Simple approach - always show upgrade modal for now
    // If user is logged in and is streamer, redirect to dashboard
    if (currentUser) {
      if (currentUser.role === 'streamer' || currentUser.role === 'admin') {
        navigate(`/streamer/${currentUser.username || currentUser.id}`);
        return;
      }
      if (currentUser.subscription === 'ultra' || currentUser.subscription === 'premium') {
        setShowStreamerRequestModal(true);
        return;
      }
    }
    // Default: show upgrade modal
    setShowUpgradeModal(true);
  };
  
  // Submit streamer request
  const handleStreamerRequest = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    
    try {
      const response = await fetch('/api/streamer-request.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(streamerForm)
      });
      
      const data = await response.json();
      if (data.success) {
        setFormSuccess(true);
        setTimeout(() => {
          setShowStreamerRequestModal(false);
          setFormSuccess(false);
          setStreamerForm({ fullName: '', email: '', username: '', channelLink: '', reason: '' });
        }, 3000);
      } else {
        alert(data.message || 'Failed to submit request');
      }
    } catch (err) {
      alert('Failed to submit request. Please try again.');
    } finally {
      setFormSubmitting(false);
    }
  };
  
  // Handle crypto payment
  const handleCryptoPayment = async () => {
    if (!currentUser) {
      window.location.href = '/login?redirect=' + encodeURIComponent('/live/streams');
      return;
    }
    
    setCryptoLoading(true);
    try {
      const response = await fetch('/api/payment/cinepay-create-order.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          plan_slug: 'ultra'
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        alert(data.error || 'Failed to create payment. Please try again.');
      }
    } catch (err) {
      console.error('Crypto payment error:', err);
      alert('Failed to process payment. Please try again.');
    } finally {
      setCryptoLoading(false);
    }
  };
  
  // Fetch streams
  const fetchStreams = useCallback(async () => {
    try {
      setLoading(true);
      const response = await streamApi.getActiveStreams();
      const realStreams = response.data.streams || [];
      setStreams(realStreams);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch streams:', err);
      setError('Failed to load streams. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    fetchStreams();
    const interval = setInterval(fetchStreams, 30000);
    return () => clearInterval(interval);
  }, [fetchStreams]);
  
  // Socket updates
  useEffect(() => {
    const socket = getSocket();
    
    socket.on('stream:started', () => fetchStreams());
    socket.on('stream:ended', (data) => {
      setStreams(prev => prev.filter(s => s.slug !== data.slug));
    });
    socket.on('viewers:update', (data) => {
      setStreams(prev => prev.map(s => 
        s.slug === data.slug ? { ...s, viewers_count: data.count } : s
      ));
    });
    
    return () => {
      socket.off('stream:started');
      socket.off('stream:ended');
      socket.off('viewers:update');
    };
  }, [fetchStreams]);
  
  // Handle stream click
  const handleStreamClick = (stream) => {
    if (!stream.slug) {
      console.error('No slug for stream:', stream);
      return;
    }
    // Use window.location for proper navigation
    window.location.href = `/live/${stream.slug}`;
  };
  
  // Use real streams only
  const displayStreams = streams;
  
  // Filter streams
  const filteredStreams = displayStreams.filter(stream => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchTitle = stream.title?.toLowerCase().includes(query);
      const matchStreamer = stream.streamer?.username?.toLowerCase().includes(query);
      const matchCategory = stream.category?.toLowerCase().includes(query);
      if (!matchTitle && !matchStreamer && !matchCategory) return false;
    }
    
    if (selectedCategory !== 'live' && selectedCategory !== 'home') {
      if (!stream.category?.toLowerCase().includes(selectedCategory)) return false;
    }
    
    return true;
  });
  
  // Sort streams
  const sortedStreams = [...filteredStreams].sort((a, b) => {
    if (a.is_live && !b.is_live) return -1;
    if (!a.is_live && b.is_live) return 1;
    
    switch (sortBy) {
      case 'recent':
        return new Date(b.started_at || 0) - new Date(a.started_at || 0);
      case 'category':
        return (a.category || '').localeCompare(b.category || '');
      default: // viewers
        return (b.viewers_count || 0) - (a.viewers_count || 0);
    }
  });
  
  // Get featured & remaining
  const featuredStream = sortedStreams.find(s => s.is_live);
  const remainingStreams = featuredStream 
    ? sortedStreams.filter(s => s.id !== featuredStream.id)
    : sortedStreams;
  
  // Stats
  const liveCount = displayStreams.filter(s => s.is_live).length;
  const totalViewers = displayStreams.reduce((sum, s) => sum + (s.viewers_count || 0), 0);
  
  // Recommended streams (shuffle for variety)
  const recommendedStreams = [...displayStreams]
    .filter(s => s.is_live)
    .sort(() => Math.random() - 0.5)
    .slice(0, 6);

  return (
    <div className={`streams-page ${sidebarOpen ? 'sidebar-open' : 'sidebar-collapsed'}`}>
      <UniversalNavbar activePage="live" />
      
      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
      />
      
      {/* Main Content Area */}
      <main className="main-content">
        {/* Header with stats & CTA */}
        <header className="page-header">
          <div className="header-left">
            <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
              <i className="fas fa-bars"></i>
            </button>
            <div className="header-title">
              <h1>
                <i className="fas fa-broadcast-tower"></i>
                Live Streams
              </h1>
              <div className="live-stats">
                <span className="stat live-stat">
                  <span className="live-pulse-dot"></span>
                  {liveCount} Live
                </span>
                <span className="stat viewers-stat">
                  <i className="fas fa-eye"></i>
                  {totalViewers.toLocaleString()} watching
                </span>
              </div>
            </div>
          </div>
          
          <div className="header-right">
            <button onClick={handleGoLiveClick} className="start-stream-btn">
              <i className="fas fa-video"></i>
              Start Streaming
            </button>
          </div>
        </header>
        
        {/* Filter Bar */}
        <FilterBar
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          sortBy={sortBy}
          onSortChange={setSortBy}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
        

        
        {/* Content */}
        <div className="content-area">
          {loading && streams.length === 0 ? (
            <LoadingSkeleton count={10} />
          ) : sortedStreams.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="fas fa-broadcast-tower"></i>
              </div>
              <h3>No streams found</h3>
              <p>Try adjusting your filters or search query</p>
              <button 
                className="empty-btn"
                onClick={() => { setSearchQuery(''); setSelectedCategory('live'); setActiveFilter('trending'); }}
              >
                <i className="fas fa-refresh"></i>
                Reset Filters
              </button>
            </div>
          ) : (
            <>
              {/* Featured Stream */}
              {featuredStream && (
                <section className="section featured-section">
                  <div className="section-header">
                    <h2>
                      <span className="section-live-dot"></span>
                      Featured Live
                    </h2>
                  </div>
                  <FeaturedStream stream={featuredStream} onClick={handleStreamClick} />
                </section>
              )}
              
              {/* Recommended Section */}
              {recommendedStreams.length > 2 && (
                <RecommendedSection 
                  streams={recommendedStreams} 
                  onStreamClick={handleStreamClick} 
                />
              )}
              
              {/* Live Now Grid */}
              {remainingStreams.filter(s => s.is_live).length > 0 && (
                <section className="section">
                  <div className="section-header">
                    <h2>
                      <i className="fas fa-broadcast-tower"></i>
                      Live Now
                    </h2>
                    <span className="section-count">
                      {remainingStreams.filter(s => s.is_live).length} streams
                    </span>
                  </div>
                  <div className="streams-grid">
                    {remainingStreams.filter(s => s.is_live).map(stream => (
                      <StreamCard 
                        key={stream.id} 
                        stream={stream} 
                        onClick={handleStreamClick}
                      />
                    ))}
                  </div>
                </section>
              )}
              
              {/* Offline/Recent */}
              {remainingStreams.filter(s => !s.is_live).length > 0 && (
                <section className="section">
                  <div className="section-header">
                    <h2>
                      <i className="fas fa-history"></i>
                      Recently Streamed
                    </h2>
                  </div>
                  <div className="streams-grid">
                    {remainingStreams.filter(s => !s.is_live).map(stream => (
                      <StreamCard 
                        key={stream.id} 
                        stream={stream} 
                        onClick={handleStreamClick}
                      />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </main>
      
      {/* Mobile Filter Sheet */}
      {mobileFiltersOpen && (
        <div className="mobile-filter-sheet">
          <div className="sheet-overlay" onClick={() => setMobileFiltersOpen(false)} />
          <div className="sheet-content">
            <div className="sheet-header">
              <h3>Filters</h3>
              <button onClick={() => setMobileFiltersOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            {/* Mobile filters content */}
          </div>
        </div>
      )}
      
      {/* Upgrade to Ultra Modal */}
      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal-content upgrade-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowUpgradeModal(false)}>
              <i className="fas fa-times"></i>
            </button>
            
            <div className="modal-icon">
              <i className="fas fa-broadcast-tower"></i>
            </div>
            
            <h2>Become a Streamer</h2>
            <p className="modal-subtitle">Upgrade to Ultra Package to unlock streaming features</p>
            
            {/* Ultra Package Card */}
            <div className="ultra-package-card">
              <div className="package-header">
                <span className="best-value-badge">Best Value</span>
                <h3>Ultra</h3>
                <div className="package-price">
                  <span className="price">€14.99</span>
                  <span className="period">/month</span>
                </div>
              </div>
              
              <div className="package-features">
                <div className="feature">
                  <i className="fas fa-check"></i>
                  <span>Go Live & Stream</span>
                </div>
                <div className="feature">
                  <i className="fas fa-check"></i>
                  <span>Receive Donations</span>
                </div>
                <div className="feature">
                  <i className="fas fa-check"></i>
                  <span>Withdraw in Crypto</span>
                </div>
                <div className="feature">
                  <i className="fas fa-check"></i>
                  <span>HD/4K Quality</span>
                </div>
                <div className="feature">
                  <i className="fas fa-check"></i>
                  <span>Watch Parties</span>
                </div>
                <div className="feature">
                  <i className="fas fa-check"></i>
                  <span>CinCommunity Access</span>
                </div>
                <div className="feature">
                  <i className="fas fa-check"></i>
                  <span>Priority Support</span>
                </div>
              </div>
              
              <div className="package-trial">
                <i className="fas fa-gift"></i>
                <span>7 Days Free Trial</span>
              </div>
            </div>
            
            <div className="payment-buttons">
              <a href="/subscribe?plan=ultra" className="upgrade-btn">
                <i className="fas fa-rocket"></i>
                Start Free Trial
              </a>
              
              <button onClick={handleCryptoPayment} className="crypto-btn" disabled={cryptoLoading}>
                {cryptoLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fab fa-bitcoin"></i>
                    Pay with Crypto
                  </>
                )}
              </button>
            </div>
            
            <p className="modal-note">
              Already Ultra? <button onClick={() => { setShowUpgradeModal(false); setShowStreamerRequestModal(true); }}>Apply for Streamer Role</button>
            </p>
            
            <p className="modal-disclaimer">
              Cancel anytime. No credit card required for trial.
            </p>
          </div>
        </div>
      )}
      
      {/* Streamer Request Modal */}
      {showStreamerRequestModal && (
        <div className="modal-overlay" onClick={() => setShowStreamerRequestModal(false)}>
          <div className="modal-content streamer-request-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowStreamerRequestModal(false)}>
              <i className="fas fa-times"></i>
            </button>
            
            {formSuccess ? (
              <div className="success-state">
                <div className="success-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h2>Request Submitted!</h2>
                <p>We'll review your application and get back to you within 24-48 hours.</p>
              </div>
            ) : (
              <>
                <div className="modal-icon streamer-icon">
                  <i className="fas fa-video"></i>
                </div>
                
                <h2>Apply to Become a Streamer</h2>
                <p className="modal-subtitle">Fill out the form below and we'll review your application</p>
                
                <div className="streamer-benefits">
                  <div className="benefit">
                    <i className="fas fa-donate"></i>
                    <div>
                      <strong>Receive Donations</strong>
                      <span>Viewers can support you with donations</span>
                    </div>
                  </div>
                  <div className="benefit">
                    <i className="fas fa-bitcoin"></i>
                    <div>
                      <strong>Withdraw in Crypto</strong>
                      <span>Cash out your earnings in cryptocurrency</span>
                    </div>
                  </div>
                </div>
                
                <form onSubmit={handleStreamerRequest} className="streamer-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Full Name *</label>
                      <input
                        type="text"
                        value={streamerForm.fullName}
                        onChange={e => setStreamerForm({...streamerForm, fullName: e.target.value})}
                        placeholder="John Doe"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        value={streamerForm.email}
                        onChange={e => setStreamerForm({...streamerForm, email: e.target.value})}
                        placeholder="john@example.com"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>CINECLUBS Username *</label>
                      <input
                        type="text"
                        value={streamerForm.username}
                        onChange={e => setStreamerForm({...streamerForm, username: e.target.value})}
                        placeholder="@yourusername"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Existing Channel (Optional)</label>
                      <input
                        type="url"
                        value={streamerForm.channelLink}
                        onChange={e => setStreamerForm({...streamerForm, channelLink: e.target.value})}
                        placeholder="Twitch/YouTube channel URL"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Why do you want to stream? *</label>
                    <textarea
                      value={streamerForm.reason}
                      onChange={e => setStreamerForm({...streamerForm, reason: e.target.value})}
                      placeholder="Tell us about yourself and what kind of content you plan to stream..."
                      rows={4}
                      required
                    />
                  </div>
                  
                  <button type="submit" className="submit-btn" disabled={formSubmitting}>
                    {formSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i>
                        Submit Application
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Mobile Bottom Navigation - Glass Effect */}
      <nav className="mobile-bottom-nav">
        <button 
          className={`nav-item ${selectedCategory === 'home' ? 'active' : ''}`}
          onClick={() => navigate('/')}
        >
          <i className="fas fa-home"></i>
          <span>Home</span>
        </button>
        <button 
          className={`nav-item ${selectedCategory === 'live' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('live')}
        >
          <i className="fas fa-broadcast-tower"></i>
          <span>Live</span>
          <span className="live-indicator"></span>
        </button>
        <button 
          className="nav-item nav-center"
          onClick={handleGoLiveClick}
        >
          <div className="go-live-btn">
            <i className="fas fa-video"></i>
          </div>
          <span>Go Live</span>
        </button>
        <button 
          className={`nav-item ${selectedCategory === 'gaming' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('gaming')}
        >
          <i className="fas fa-gamepad"></i>
          <span>Gaming</span>
        </button>
        <button 
          className="nav-item"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <i className="fas fa-th-large"></i>
          <span>More</span>
        </button>
      </nav>
      
      {/* Styles */}
      <style>{`
        /* ===== CINECLUBS STREAMS PAGE - PREMIUM DESIGN ===== */
        
        /* === BASE LAYOUT === */
        .streams-page {
          min-height: 100vh;
          width: 100%;
          max-width: 100vw;
          background: #0a0a0b;
          color: #fff;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          display: flex;
          overflow-x: hidden;
          box-sizing: border-box;
        }
        
        *, *::before, *::after {
          box-sizing: border-box;
        }
        
        .streams-page.sidebar-open .main-content {
          margin-left: 220px;
          width: calc(100% - 220px);
        }
        
        .streams-page.sidebar-collapsed .main-content {
          margin-left: 64px;
          width: calc(100% - 64px);
        }
        
        /* === SIDEBAR === */
        .sidebar {
          position: fixed;
          top: 56px;
          left: 0;
          bottom: 0;
          width: 220px;
          background: #0f0f10;
          border-right: 1px solid rgba(255,255,255,0.06);
          z-index: 100;
          transition: width 0.2s ease, transform 0.2s ease;
          overflow-y: auto;
          overflow-x: hidden;
        }
        
        .sidebar.collapsed {
          width: 64px;
        }
        
        .sidebar-overlay {
          display: none;
        }
        
        .sidebar-content {
          padding: 8px 6px;
        }
        
        .sidebar-item {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 10px 14px;
          background: transparent;
          border: none;
          border-radius: 8px;
          color: rgba(255,255,255,0.7);
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s ease;
          text-align: left;
          white-space: nowrap;
        }
        
        .sidebar-item:hover {
          background: rgba(255,255,255,0.06);
          color: #fff;
        }
        
        .sidebar-item.active {
          background: linear-gradient(135deg, rgba(0,240,255,0.08) 0%, rgba(112,0,255,0.05) 100%);
          color: #fff;
          position: relative;
          overflow: hidden;
        }
        
        .sidebar-item.active::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 10px;
          padding: 1px;
          background: linear-gradient(135deg, rgba(0,240,255,0.3), rgba(112,0,255,0.2));
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
        }
        
        .sidebar-item.active::after {
          content: '';
          position: absolute;
          top: 0;
          left: 10%;
          width: 80%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
        }
        
        .sidebar-item.active i {
          color: #00f0ff;
          filter: drop-shadow(0 0 6px rgba(0,240,255,0.5));
        }
        
        .sidebar-item i {
          width: 20px;
          text-align: center;
          font-size: 1rem;
          flex-shrink: 0;
        }
        
        .sidebar-label {
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .sidebar.collapsed .sidebar-label {
          display: none;
        }
        
        .sidebar.collapsed .sidebar-item {
          justify-content: center;
          padding: 12px;
        }
        
        .sidebar-divider {
          height: 1px;
          background: rgba(255,255,255,0.08);
          margin: 12px 16px;
        }
        
        /* === MAIN CONTENT === */
        .main-content {
          flex: 1;
          min-height: 100vh;
          padding-top: 56px;
          transition: margin-left 0.2s ease, width 0.2s ease;
          overflow-x: hidden;
          max-width: 100%;
        }
        
        /* === PAGE HEADER === */
        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 24px;
          background: linear-gradient(180deg, rgba(229,9,20,0.08) 0%, transparent 100%);
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }
        
        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .sidebar-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 10px;
          color: #fff;
          cursor: pointer;
          transition: all 0.15s;
        }
        
        .sidebar-toggle:hover {
          background: rgba(255,255,255,0.1);
        }
        
        .header-title h1 {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 4px;
        }
        
        .header-title h1 i {
          color: #e50914;
        }
        
        .live-stats {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .stat {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.85rem;
          color: rgba(255,255,255,0.6);
        }
        
        .live-stat {
          color: #e50914;
          font-weight: 600;
        }
        
        .live-pulse-dot {
          width: 8px;
          height: 8px;
          background: #e50914;
          border-radius: 50%;
          animation: livePulse 1.5s ease-in-out infinite;
        }
        
        @keyframes livePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.9); }
        }
        
        .header-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .start-stream-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: linear-gradient(135deg, #e50914 0%, #8b008b 100%);
          border: none;
          border-radius: 24px;
          color: #fff;
          font-size: 0.9rem;
          font-weight: 600;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 4px 20px rgba(229,9,20,0.3);
        }
        
        .start-stream-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 28px rgba(229,9,20,0.4);
        }
        
        /* === FILTER BAR === */
        .filter-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 12px 24px;
          background: rgba(0,0,0,0.3);
          border-bottom: 1px solid rgba(255,255,255,0.05);
          position: sticky;
          top: 56px;
          z-index: 80;
          backdrop-filter: blur(10px);
          width: 100%;
          max-width: 100%;
          overflow: hidden;
        }
        
        .filter-chips {
          display: flex;
          align-items: center;
          gap: 8px;
          overflow-x: auto;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          padding: 4px 0;
          flex: 1;
          min-width: 0;
        }
        
        .filter-chips::-webkit-scrollbar {
          display: none;
        }
        
        .filter-chip {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 20px;
          color: rgba(255,255,255,0.75);
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          white-space: nowrap;
          flex-shrink: 0;
        }
        
        .filter-chip:hover {
          background: rgba(255,255,255,0.08);
          color: #fff;
        }
        
        .filter-chip.active {
          background: linear-gradient(135deg, #e50914 0%, #b20710 100%);
          border-color: transparent;
          color: #fff;
        }
        
        .filter-chip i {
          font-size: 0.8rem;
        }
        
        .filter-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
        }
        
        .search-box {
          position: relative;
          width: 280px;
        }
        
        .search-box input {
          width: 100%;
          height: 40px;
          padding: 0 40px 0 44px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 20px;
          color: #fff;
          font-size: 0.9rem;
          transition: all 0.15s;
        }
        
        .search-box input:focus {
          outline: none;
          background: rgba(255,255,255,0.08);
          border-color: #e50914;
        }
        
        .search-box input::placeholder {
          color: rgba(255,255,255,0.4);
        }
        
        .search-box > i {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255,255,255,0.4);
        }
        
        .search-clear {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: rgba(255,255,255,0.4);
          cursor: pointer;
          padding: 4px;
        }
        
        .search-clear:hover {
          color: #fff;
        }
        
        .sort-dropdown-wrapper {
          position: relative;
        }
        
        .sort-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          color: rgba(255,255,255,0.8);
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.15s;
        }
        
        .sort-btn:hover {
          background: rgba(255,255,255,0.08);
        }
        
        .sort-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          min-width: 180px;
          background: #1a1a1c;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px;
          padding: 8px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.5);
          z-index: 100;
        }
        
        .sort-option {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 10px 14px;
          background: transparent;
          border: none;
          border-radius: 8px;
          color: rgba(255,255,255,0.8);
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.15s;
          text-align: left;
        }
        
        .sort-option:hover {
          background: rgba(255,255,255,0.06);
        }
        
        .sort-option.active {
          color: #e50914;
        }
        
        /* === DEMO NOTICE === */
        .demo-notice {
          display: flex;
          align-items: center;
          gap: 16px;
          margin: 16px 24px;
          padding: 14px 20px;
          background: linear-gradient(135deg, rgba(229,9,20,0.1) 0%, rgba(139,0,139,0.08) 100%);
          border: 1px solid rgba(229,9,20,0.2);
          border-radius: 12px;
        }
        
        .demo-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(229,9,20,0.2);
          border-radius: 10px;
          color: #e50914;
          font-size: 1.1rem;
        }
        
        .demo-text {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        
        .demo-text strong {
          font-size: 0.9rem;
          color: #fff;
        }
        
        .demo-text span {
          font-size: 0.8rem;
          color: rgba(255,255,255,0.6);
        }
        
        .demo-cta {
          padding: 8px 18px;
          background: #e50914;
          border-radius: 20px;
          color: #fff;
          font-size: 0.85rem;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s;
        }
        
        .demo-cta:hover {
          background: #ff1a1a;
        }
        
        /* === CONTENT AREA === */
        .content-area {
          padding: 20px 24px 40px;
          width: 100%;
          max-width: 100%;
          overflow: hidden;
        }
        
        /* === SECTIONS === */
        .section {
          margin-bottom: 36px;
        }
        
        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }
        
        .section-header h2 {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 1.2rem;
          font-weight: 600;
          margin: 0;
        }
        
        .section-header h2 i {
          color: #e50914;
          font-size: 1rem;
        }
        
        .section-live-dot {
          width: 10px;
          height: 10px;
          background: #e50914;
          border-radius: 50%;
          animation: livePulse 1.5s infinite;
        }
        
        .section-count {
          font-size: 0.85rem;
          color: rgba(255,255,255,0.5);
        }
        
        /* === FEATURED STREAM === */
        .featured-stream {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          cursor: pointer;
          background: #141416;
        }
        
        .featured-thumbnail {
          position: relative;
          aspect-ratio: 21/9;
          overflow: hidden;
        }
        
        .featured-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.4s ease;
        }
        
        .featured-stream:hover .featured-thumbnail img {
          transform: scale(1.03);
        }
        
        .featured-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #1a1a2e, #0f0f0f);
          font-size: 4rem;
          color: rgba(255,255,255,0.1);
        }
        
        .featured-gradient {
          position: absolute;
          inset: 0;
          background: linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.3) 50%, transparent 100%);
        }
        
        .featured-top-badges {
          position: absolute;
          top: 16px;
          left: 16px;
          display: flex;
          gap: 10px;
          z-index: 2;
        }
        
        .live-badge-featured {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: #e50914;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.5px;
        }
        
        .live-pulse {
          width: 8px;
          height: 8px;
          background: #fff;
          border-radius: 50%;
          animation: livePulse 1.5s infinite;
        }
        
        .duration-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: rgba(0,0,0,0.6);
          backdrop-filter: blur(8px);
          border-radius: 6px;
          font-size: 0.8rem;
        }
        
        .featured-bottom {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 24px;
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          z-index: 2;
        }
        
        .featured-info-overlay {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .featured-avatar {
          position: relative;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          overflow: visible;
          flex-shrink: 0;
        }
        
        .featured-avatar img,
        .featured-avatar .avatar-letter {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
        }
        
        .avatar-letter {
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #e50914, #8b008b);
          font-size: 1.5rem;
          font-weight: 700;
        }
        
        .avatar-live-ring {
          position: absolute;
          inset: -3px;
          border: 2px solid #e50914;
          border-radius: 50%;
          animation: ringPulse 2s infinite;
        }
        
        @keyframes ringPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.7; }
        }
        
        .featured-text {
          flex: 1;
          min-width: 0;
        }
        
        .featured-title {
          font-size: 1.4rem;
          font-weight: 700;
          margin: 0 0 8px;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .featured-meta {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
          font-size: 0.9rem;
          color: rgba(255,255,255,0.7);
        }
        
        .meta-dot {
          opacity: 0.5;
        }
        
        .category-tag {
          padding: 4px 10px;
          background: rgba(255,255,255,0.1);
          border-radius: 12px;
          font-size: 0.8rem;
        }
        
        .watch-btn {
          padding: 12px 28px;
          background: linear-gradient(135deg, #e50914 0%, #8b008b 100%);
          border: none;
          border-radius: 24px;
          color: #fff;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          opacity: 0;
          transform: translateY(10px);
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .watch-btn.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        .watch-btn:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 8px 24px rgba(229,9,20,0.4);
        }
        
        .featured-hover-play {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1;
          animation: fadeIn 0.2s ease;
        }
        
        .play-circle {
          width: 80px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(229,9,20,0.9);
          border-radius: 50%;
          font-size: 1.8rem;
          box-shadow: 0 8px 32px rgba(229,9,20,0.4);
        }
        
        /* === RECOMMENDED SECTION === */
        .recommended-section {
          margin-bottom: 36px;
        }
        
        .scroll-buttons {
          display: flex;
          gap: 8px;
        }
        
        .scroll-btn {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 50%;
          color: #fff;
          cursor: pointer;
          transition: all 0.15s;
        }
        
        .scroll-btn:hover {
          background: rgba(255,255,255,0.1);
        }
        
        .recommended-scroll {
          display: flex;
          gap: 16px;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          scrollbar-width: none;
          padding: 8px 0;
        }
        
        .recommended-scroll::-webkit-scrollbar {
          display: none;
        }
        
        .recommended-card {
          flex-shrink: 0;
          width: 320px;
          background: #141416;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.2s;
          scroll-snap-align: start;
        }
        
        .recommended-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0,0,0,0.4);
        }
        
        .rec-thumbnail {
          position: relative;
          aspect-ratio: 16/9;
        }
        
        .rec-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .rec-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #1a1a1c;
          color: rgba(255,255,255,0.2);
          font-size: 2rem;
        }
        
        .rec-live {
          position: absolute;
          top: 10px;
          left: 10px;
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 4px 8px;
          background: #e50914;
          border-radius: 4px;
          font-size: 0.65rem;
          font-weight: 700;
        }
        
        .rec-viewers {
          position: absolute;
          bottom: 10px;
          right: 10px;
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 4px 8px;
          background: rgba(0,0,0,0.7);
          border-radius: 4px;
          font-size: 0.75rem;
        }
        
        .rec-info {
          padding: 12px 14px;
        }
        
        .rec-info h4 {
          font-size: 0.95rem;
          font-weight: 600;
          margin: 0 0 4px;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .rec-info p {
          font-size: 0.8rem;
          color: rgba(255,255,255,0.5);
          margin: 0;
        }
        
        /* === STREAMS GRID - PROPER SIZES === */
        .streams-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 16px;
          width: 100%;
        }
        
        /* === STREAM CARD - PROPER SIZES === */
        .stream-card {
          background: #141416;
          border-radius: 10px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid transparent;
          min-width: 0;
        }
        
        .stream-card:hover {
          transform: translateY(-3px);
          border-color: rgba(0,240,255,0.2);
          box-shadow: 0 8px 24px rgba(0,0,0,0.4);
        }
        
        .card-thumbnail {
          position: relative;
          aspect-ratio: 16/9;
          overflow: hidden;
          background: #0a0a0b;
        }
        
        .card-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }
        
        .stream-card:hover .card-thumbnail img {
          transform: scale(1.03);
        }
        
        .thumbnail-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #1a1a2e, #0f0f0f);
          color: rgba(255,255,255,0.15);
          font-size: 1.5rem;
        }
        
        .card-live-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 3px 6px;
          background: #e50914;
          border-radius: 3px;
          font-size: 0.6rem;
          font-weight: 700;
          letter-spacing: 0.3px;
        }
        
        .live-dot {
          width: 5px;
          height: 5px;
          background: #fff;
          border-radius: 50%;
          animation: livePulse 1.5s infinite;
        }
        
        .card-offline-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          padding: 3px 6px;
          background: rgba(80,80,80,0.9);
          border-radius: 3px;
          font-size: 0.6rem;
          font-weight: 600;
        }
        
        .card-viewers {
          position: absolute;
          top: 8px;
          right: 8px;
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 3px 6px;
          background: rgba(0,0,0,0.75);
          backdrop-filter: blur(4px);
          border-radius: 3px;
          font-size: 0.65rem;
        }
        
        .card-duration {
          position: absolute;
          bottom: 8px;
          right: 8px;
          padding: 2px 5px;
          background: rgba(0,0,0,0.8);
          border-radius: 3px;
          font-size: 0.6rem;
          font-variant-numeric: tabular-nums;
        }
        
        .card-hover-overlay {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,240,255,0.15);
          opacity: 0;
          transition: opacity 0.2s;
        }
        
        .card-hover-overlay.visible {
          opacity: 1;
        }
        
        .hover-play-btn {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.95);
          border-radius: 50%;
          color: #e50914;
          font-size: 1rem;
        }
        
        .card-follow-btn {
          position: absolute;
          top: 8px;
          right: 40px;
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.7);
          backdrop-filter: blur(4px);
          border: none;
          border-radius: 50%;
          color: #fff;
          font-size: 0.65rem;
          cursor: pointer;
          opacity: 0;
          transition: all 0.2s;
        }
        
        .card-follow-btn.visible {
          opacity: 1;
        }
        
        .card-follow-btn:hover {
          background: #e50914;
        }
        
        .card-follow-btn.following {
          background: #e50914;
          color: #fff;
        }
        
        .card-info {
          display: flex;
          gap: 10px;
          padding: 10px;
        }
        
        .card-avatar {
          position: relative;
          flex-shrink: 0;
        }
        
        .card-avatar img,
        .card-avatar .avatar-placeholder {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          object-fit: cover;
        }
        
        .avatar-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #00f0ff, #7000ff);
          color: #fff;
          font-weight: 700;
          font-size: 0.75rem;
        }
        
        .online-dot {
          position: absolute;
          bottom: -1px;
          right: -1px;
          width: 8px;
          height: 8px;
          background: #00ff00;
          border: 2px solid #141416;
          border-radius: 50%;
        }
        
        .card-details {
          flex: 1;
          min-width: 0;
        }
        
        .card-title {
          font-size: 0.8rem;
          font-weight: 600;
          margin: 0 0 3px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.25;
          word-break: break-word;
        }
        
        .card-streamer {
          font-size: 0.7rem;
          color: rgba(255,255,255,0.55);
          margin: 0 0 4px;
        }
        
        .card-category {
          display: inline-block;
          padding: 2px 6px;
          background: rgba(255,255,255,0.06);
          border-radius: 8px;
          font-size: 0.6rem;
          color: rgba(255,255,255,0.6);
        }
        
        /* === EMPTY STATE === */
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          text-align: center;
        }
        
        .empty-icon {
          width: 80px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.05);
          border-radius: 50%;
          font-size: 2rem;
          color: rgba(255,255,255,0.2);
          margin-bottom: 24px;
        }
        
        .empty-state h3 {
          font-size: 1.4rem;
          margin: 0 0 8px;
        }
        
        .empty-state p {
          color: rgba(255,255,255,0.5);
          margin: 0 0 24px;
        }
        
        .empty-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: #e50914;
          border: none;
          border-radius: 24px;
          color: #fff;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .empty-btn:hover {
          background: #ff1a1a;
          transform: scale(1.05);
        }
        
        /* === SKELETON LOADING === */
        .skeleton .card-thumbnail,
        .skeleton-thumb {
          background: linear-gradient(90deg, #1a1a1c 25%, #252528 50%, #1a1a1c 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
        
        .skeleton-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #252528;
        }
        
        .skeleton-title {
          height: 16px;
          width: 80%;
          background: #252528;
          border-radius: 4px;
          margin-bottom: 8px;
        }
        
        .skeleton-name {
          height: 12px;
          width: 50%;
          background: #1f1f22;
          border-radius: 4px;
        }
        
        @keyframes shimmer {
          to { background-position: -200% 0; }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        /* === MOBILE FILTER SHEET === */
        .mobile-filter-sheet {
          display: none;
        }
        
        /* === MODALS === */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.85);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          padding: 20px;
          animation: fadeIn 0.2s ease;
        }
        
        .modal-content {
          background: linear-gradient(145deg, #1a1a1c 0%, #0f0f10 100%);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 20px;
          padding: 32px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          animation: modalSlide 0.3s ease;
        }
        
        @keyframes modalSlide {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        
        .modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.05);
          border: none;
          border-radius: 50%;
          color: rgba(255,255,255,0.6);
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .modal-close:hover {
          background: rgba(255,255,255,0.1);
          color: #fff;
        }
        
        .modal-icon {
          width: 80px;
          height: 80px;
          margin: 0 auto 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, rgba(229,9,20,0.2) 0%, rgba(255,215,0,0.1) 100%);
          border-radius: 50%;
          font-size: 2rem;
          color: #ffd700;
        }
        
        .modal-icon.streamer-icon {
          background: linear-gradient(135deg, rgba(229,9,20,0.2) 0%, rgba(139,0,139,0.1) 100%);
          color: #e50914;
        }
        
        .modal-content h2 {
          text-align: center;
          font-size: 1.5rem;
          margin: 0 0 8px;
        }
        
        .modal-subtitle {
          text-align: center;
          color: rgba(255,255,255,0.6);
          font-size: 0.9rem;
          margin: 0 0 28px;
        }
        
        .features-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 28px;
        }
        
        .feature-item {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 16px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px;
        }
        
        .feature-item i {
          width: 24px;
          text-align: center;
          color: #e50914;
          font-size: 1rem;
        }
        
        .feature-item span {
          font-size: 0.9rem;
          color: rgba(255,255,255,0.85);
        }
        
        .upgrade-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 16px 24px;
          background: linear-gradient(135deg, #e50914 0%, #b20710 100%);
          border: none;
          border-radius: 12px;
          color: #fff;
          font-size: 1rem;
          font-weight: 600;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 4px 20px rgba(229,9,20,0.3);
        }
        
        .upgrade-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 28px rgba(229,9,20,0.4);
        }
        
        .modal-note {
          text-align: center;
          margin-top: 20px;
          font-size: 0.85rem;
          color: rgba(255,255,255,0.5);
        }
        
        .modal-note button {
          background: none;
          border: none;
          color: #e50914;
          cursor: pointer;
          font-size: 0.85rem;
          text-decoration: underline;
        }
        
        .modal-disclaimer {
          text-align: center;
          margin-top: 12px;
          font-size: 0.75rem;
          color: rgba(255,255,255,0.4);
        }
        
        /* Ultra Package Card */
        .ultra-package-card {
          background: linear-gradient(145deg, rgba(229,9,20,0.12) 0%, rgba(139,0,139,0.08) 100%);
          border: 2px solid rgba(229,9,20,0.3);
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 20px;
          position: relative;
          overflow: hidden;
        }
        
        .ultra-package-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #e50914, #ffd700, #e50914);
        }
        
        .package-header {
          text-align: center;
          margin-bottom: 20px;
        }
        
        .best-value-badge {
          display: inline-block;
          padding: 4px 12px;
          background: linear-gradient(135deg, #ffd700, #ffaa00);
          border-radius: 20px;
          font-size: 0.7rem;
          font-weight: 700;
          color: #000;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }
        
        .package-header h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 8px;
          background: linear-gradient(135deg, #fff, #e50914);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .package-price {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 4px;
        }
        
        .package-price .price {
          font-size: 2rem;
          font-weight: 700;
          color: #fff;
        }
        
        .package-price .period {
          font-size: 0.9rem;
          color: rgba(255,255,255,0.5);
        }
        
        .package-features {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 16px;
        }
        
        .package-features .feature {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.85rem;
          color: rgba(255,255,255,0.85);
        }
        
        .package-features .feature i {
          color: #00c864;
          font-size: 0.75rem;
        }
        
        .package-trial {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px;
          background: rgba(0,200,100,0.1);
          border: 1px solid rgba(0,200,100,0.2);
          border-radius: 10px;
          color: #00c864;
          font-size: 0.85rem;
          font-weight: 600;
        }
        
        .package-trial i {
          font-size: 1rem;
        }
        
        /* Payment Buttons */
        .payment-buttons {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 16px;
        }
        
        .crypto-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 14px 24px;
          background: linear-gradient(135deg, #f7931a 0%, #ff9500 100%);
          border: none;
          border-radius: 12px;
          color: #fff;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 4px 20px rgba(247,147,26,0.3);
        }
        
        .crypto-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 28px rgba(247,147,26,0.4);
        }
        
        .crypto-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .crypto-btn i {
          font-size: 1.1rem;
        }
        
        /* Streamer Request Modal */
        .streamer-request-modal {
          max-width: 600px;
        }
        
        .streamer-benefits {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
        }
        
        .benefit {
          flex: 1;
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          background: rgba(229,9,20,0.08);
          border: 1px solid rgba(229,9,20,0.15);
          border-radius: 12px;
        }
        
        .benefit i {
          color: #e50914;
          font-size: 1.2rem;
          margin-top: 2px;
        }
        
        .benefit div {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        
        .benefit strong {
          font-size: 0.9rem;
          color: #fff;
        }
        
        .benefit span {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.5);
        }
        
        .streamer-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .form-group label {
          font-size: 0.85rem;
          font-weight: 500;
          color: rgba(255,255,255,0.8);
        }
        
        .form-group input,
        .form-group textarea {
          padding: 12px 14px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          color: #fff;
          font-size: 0.9rem;
          transition: all 0.2s;
        }
        
        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #e50914;
          background: rgba(255,255,255,0.08);
        }
        
        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: rgba(255,255,255,0.3);
        }
        
        .form-group textarea {
          resize: vertical;
          min-height: 100px;
        }
        
        .submit-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 14px 24px;
          background: linear-gradient(135deg, #e50914 0%, #b20710 100%);
          border: none;
          border-radius: 12px;
          color: #fff;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 8px;
        }
        
        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(229,9,20,0.4);
        }
        
        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        /* Success State */
        .success-state {
          text-align: center;
          padding: 40px 20px;
        }
        
        .success-icon {
          width: 80px;
          height: 80px;
          margin: 0 auto 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,200,100,0.1);
          border-radius: 50%;
          font-size: 2.5rem;
          color: #00c864;
        }
        
        .success-state h2 {
          color: #00c864;
          margin-bottom: 12px;
        }
        
        .success-state p {
          color: rgba(255,255,255,0.6);
        }
        
        /* Modal Responsive */
        @media (max-width: 600px) {
          .modal-content {
            padding: 24px 20px;
            border-radius: 16px;
          }
          
          .modal-icon {
            width: 64px;
            height: 64px;
            font-size: 1.5rem;
          }
          
          .modal-content h2 {
            font-size: 1.25rem;
          }
          
          .modal-subtitle {
            font-size: 0.85rem;
          }
          
          .streamer-benefits {
            flex-direction: column;
          }
          
          .form-row {
            grid-template-columns: 1fr;
          }
          
          .feature-item {
            padding: 12px 14px;
          }
        }
        
        /* === MOBILE BOTTOM NAVIGATION - iOS Glass Effect === */
        .mobile-bottom-nav {
          display: none;
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          height: 70px;
          background: rgba(15, 15, 16, 0.85);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-top: 1px solid rgba(255,255,255,0.08);
          z-index: 1000;
          padding: 0 8px;
          padding-bottom: env(safe-area-inset-bottom, 0);
        }
        
        .mobile-bottom-nav {
          display: none;
          justify-content: space-around;
          align-items: center;
        }
        
        .nav-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          padding: 8px 12px;
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.5);
          font-size: 0.65rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          min-width: 56px;
        }
        
        .nav-item i {
          font-size: 1.2rem;
          transition: all 0.2s ease;
        }
        
        .nav-item.active {
          color: #e50914;
        }
        
        .nav-item.active i {
          transform: scale(1.1);
          filter: drop-shadow(0 0 8px rgba(229,9,20,0.5));
        }
        
        .nav-item .live-indicator {
          position: absolute;
          top: 6px;
          right: 12px;
          width: 6px;
          height: 6px;
          background: #e50914;
          border-radius: 50%;
          animation: livePulse 1.5s infinite;
        }
        
        .nav-item.nav-center {
          padding-top: 0;
        }
        
        .nav-item .go-live-btn {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #e50914 0%, #b20710 100%);
          border-radius: 50%;
          margin-top: -20px;
          box-shadow: 0 4px 20px rgba(229,9,20,0.4);
          transition: all 0.2s ease;
        }
        
        .nav-item .go-live-btn i {
          color: #fff;
          font-size: 1.1rem;
        }
        
        .nav-item:active .go-live-btn {
          transform: scale(0.95);
        }
        
        /* === SIDEBAR HEADER (Mobile) === */
        .sidebar-header {
          display: none;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        
        .sidebar-title {
          font-size: 1.1rem;
          font-weight: 600;
        }
        
        .sidebar-close {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255,255,255,0.05);
          border: none;
          border-radius: 50%;
          color: #fff;
          cursor: pointer;
        }
        
        /* === RESPONSIVE - LAPTOP (4 cards) === */
        @media (max-width: 1400px) {
          .streams-page.sidebar-open .main-content {
            margin-left: 200px;
          }
          
          .streams-page.sidebar-collapsed .main-content {
            margin-left: 56px;
          }
          
          .sidebar {
            width: 200px;
          }
          
          .sidebar.collapsed {
            width: 56px;
          }
          
          .streams-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
          }
          
          .featured-thumbnail {
            aspect-ratio: 2/1;
          }
        }
        
        /* === RESPONSIVE - SMALL LAPTOP === */
        @media (max-width: 1200px) {
          .streams-page.sidebar-open .main-content,
          .streams-page.sidebar-collapsed .main-content {
            margin-left: 0;
            width: 100%;
          }
          
          .sidebar {
            transform: translateX(-100%);
            width: 260px;
            top: 0;
            height: 100vh;
            z-index: 1001;
            background: rgba(15, 15, 16, 0.98);
            backdrop-filter: blur(20px);
          }
          
          .sidebar.open {
            transform: translateX(0);
          }
          
          .sidebar-header {
            display: flex;
          }
          
          .sidebar-overlay {
            display: block;
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.7);
            backdrop-filter: blur(4px);
            z-index: 1000;
          }
          
          .streams-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 10px;
          }
        }
        
        /* === RESPONSIVE - TABLET (3 cards) === */
        @media (max-width: 1000px) {
          .streams-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
          }
          
          .search-box {
            width: 180px;
          }
          
          .filter-chips {
            max-width: 45%;
          }
          
          .recommended-card {
            width: 240px;
          }
          
          .page-header {
            padding: 12px 16px;
          }
          
          .header-title h1 {
            font-size: 1.2rem;
          }
        }
        
        /* === RESPONSIVE - MOBILE (2 cards) === */
        @media (max-width: 768px) {
          /* Show bottom nav, hide header stream button */
          .mobile-bottom-nav {
            display: flex;
          }
          
          .sidebar-toggle {
            display: flex;
          }
          
          .start-stream-btn {
            display: none;
          }
          
          .main-content {
            padding-bottom: 75px;
          }
          
          .page-header {
            padding: 8px 12px;
            gap: 6px;
          }
          
          .header-title h1 {
            font-size: 0.95rem;
          }
          
          .header-title h1 i {
            display: none;
          }
          
          .live-stats {
            gap: 6px;
          }
          
          .stat {
            font-size: 0.6rem;
          }
          
          .filter-bar {
            padding: 6px 10px;
            gap: 6px;
            top: 56px;
          }
          
          .filter-chips {
            order: 1;
            max-width: 100%;
            width: 100%;
            gap: 5px;
            overflow-x: auto;
            flex-wrap: nowrap;
            padding-bottom: 2px;
          }
          
          .filter-chip {
            padding: 5px 10px;
            font-size: 0.65rem;
            border-radius: 14px;
            flex-shrink: 0;
          }
          
          .filter-chip i {
            font-size: 0.65rem;
          }
          
          .filter-actions {
            display: none;
          }
          
          .demo-notice {
            margin: 8px 10px;
            padding: 8px;
            gap: 6px;
          }
          
          .demo-icon {
            width: 26px;
            height: 26px;
            font-size: 0.75rem;
          }
          
          .demo-text strong {
            font-size: 0.7rem;
          }
          
          .demo-text span {
            font-size: 0.6rem;
          }
          
          .demo-cta {
            width: 100%;
            text-align: center;
            padding: 5px 10px;
            font-size: 0.65rem;
          }
          
          .content-area {
            padding: 8px 10px 80px;
          }
          
          .section {
            margin-bottom: 16px;
          }
          
          .section-header {
            margin-bottom: 8px;
          }
          
          .section-header h2 {
            font-size: 0.85rem;
          }
          
          .section-count {
            font-size: 0.7rem;
          }
          
          .streams-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 6px;
          }
          
          .stream-card {
            border-radius: 6px;
          }
          
          .card-live-badge {
            top: 4px;
            left: 4px;
            padding: 2px 4px;
            font-size: 0.45rem;
            gap: 2px;
            border-radius: 2px;
          }
          
          .live-dot {
            width: 3px;
            height: 3px;
          }
          
          .card-viewers {
            top: 4px;
            right: 4px;
            padding: 2px 4px;
            font-size: 0.45rem;
            gap: 2px;
          }
          
          .card-duration {
            bottom: 4px;
            right: 4px;
            padding: 2px 3px;
            font-size: 0.45rem;
          }
          
          .card-info {
            padding: 6px;
            gap: 6px;
          }
          
          .card-avatar img,
          .card-avatar .avatar-placeholder {
            width: 24px;
            height: 24px;
          }
          
          .avatar-placeholder {
            font-size: 0.55rem;
          }
          
          .online-dot {
            width: 5px;
            height: 5px;
            border-width: 1px;
          }
          
          .card-title {
            font-size: 0.65rem;
            line-height: 1.15;
            -webkit-line-clamp: 2;
          }
          
          .card-streamer {
            font-size: 0.55rem;
            margin-bottom: 0;
          }
          
          .card-category {
            display: none;
          }
          
          /* Featured stream mobile */
          .featured-section {
            margin-bottom: 14px;
          }
          
          .featured-thumbnail {
            aspect-ratio: 16/9;
            border-radius: 8px;
          }
          
          .featured-top-badges {
            top: 6px;
            left: 6px;
            gap: 6px;
          }
          
          .live-badge-featured {
            padding: 3px 6px;
            font-size: 0.5rem;
          }
          
          .live-pulse {
            width: 5px;
            height: 5px;
          }
          
          .duration-badge {
            padding: 3px 6px;
            font-size: 0.5rem;
          }
          
          .featured-bottom {
            padding: 10px;
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
          }
          
          .featured-info-overlay {
            gap: 10px;
          }
          
          .featured-avatar {
            width: 32px;
            height: 32px;
          }
          
          .avatar-letter {
            font-size: 0.9rem;
          }
          
          .avatar-live-ring {
            inset: -2px;
          }
          
          .featured-title {
            font-size: 0.8rem;
            -webkit-line-clamp: 2;
            margin-bottom: 4px;
          }
          
          .featured-meta {
            font-size: 0.6rem;
            gap: 4px;
          }
          
          .category-tag {
            padding: 2px 6px;
            font-size: 0.55rem;
          }
          
          .watch-btn {
            display: none;
          }
          
          .play-circle {
            width: 40px;
            height: 40px;
            font-size: 0.9rem;
          }
          
          /* Recommended mobile */
          .recommended-section {
            margin-bottom: 14px;
          }
          
          .section-title {
            font-size: 0.85rem;
          }
          
          .scroll-buttons {
            display: none;
          }
          
          .recommended-scroll {
            gap: 8px;
            padding: 2px 0;
          }
          
          .recommended-card {
            width: 150px;
            border-radius: 6px;
          }
          
          .rec-thumbnail {
            border-radius: 6px 6px 0 0;
          }
          
          .rec-live {
            top: 6px;
            left: 6px;
            padding: 2px 4px;
            font-size: 0.45rem;
          }
          
          .rec-viewers {
            bottom: 6px;
            right: 6px;
            padding: 2px 4px;
            font-size: 0.45rem;
          }
          
          .rec-info {
            padding: 6px 8px;
          }
          
          .rec-info h4 {
            font-size: 0.65rem;
          }
          
          .rec-info p {
            font-size: 0.55rem;
          }
          
          /* Mobile bottom nav */
          .mobile-bottom-nav {
            height: 60px;
          }
          
          .nav-item {
            font-size: 0.55rem;
            min-width: 50px;
            padding: 6px 8px;
          }
          
          .nav-item i {
            font-size: 1rem;
          }
          
          .nav-item .go-live-btn {
            width: 44px;
            height: 44px;
            margin-top: -18px;
          }
          
          .nav-item .go-live-btn i {
            font-size: 0.95rem;
          }
        }
        
        /* === RESPONSIVE - SMALL MOBILE === */
        @media (max-width: 480px) {
          .page-header {
            padding: 6px 10px;
          }
          
          .sidebar-toggle {
            width: 32px;
            height: 32px;
          }
          
          .header-title h1 {
            font-size: 0.85rem;
          }
          
          .stat {
            font-size: 0.55rem;
          }
          
          .live-pulse-dot {
            width: 6px;
            height: 6px;
          }
          
          .filter-bar {
            padding: 5px 8px;
          }
          
          .filter-chip {
            padding: 4px 7px;
            font-size: 0.6rem;
          }
          
          .filter-chip i {
            font-size: 0.55rem;
          }
          
          .filter-chip span {
            display: none;
          }
          
          .filter-chip.active span {
            display: inline;
          }
          
          .content-area {
            padding: 6px 8px 70px;
          }
          
          .section {
            margin-bottom: 12px;
          }
          
          .section-header h2 {
            font-size: 0.75rem;
          }
          
          .streams-grid {
            gap: 5px;
          }
          
          .stream-card {
            border-radius: 5px;
          }
          
          .card-thumbnail {
            border-radius: 5px 5px 0 0;
          }
          
          .card-live-badge {
            top: 3px;
            left: 3px;
            padding: 1px 3px;
            font-size: 0.4rem;
            border-radius: 2px;
          }
          
          .live-dot {
            width: 3px;
            height: 3px;
          }
          
          .card-viewers {
            top: 3px;
            right: 3px;
            padding: 1px 3px;
            font-size: 0.4rem;
          }
          
          .card-duration {
            bottom: 3px;
            right: 3px;
            padding: 1px 2px;
            font-size: 0.4rem;
          }
          
          .card-info {
            padding: 5px;
            gap: 5px;
          }
          
          .card-avatar img,
          .card-avatar .avatar-placeholder {
            width: 20px;
            height: 20px;
          }
          
          .avatar-placeholder {
            font-size: 0.5rem;
          }
          
          .online-dot {
            width: 4px;
            height: 4px;
          }
          
          .card-title {
            font-size: 0.58rem;
            -webkit-line-clamp: 1;
            line-height: 1.1;
          }
          
          .card-streamer {
            font-size: 0.48rem;
          }
          
          /* Featured small mobile */
          .featured-section {
            margin-bottom: 10px;
          }
          
          .featured-thumbnail {
            border-radius: 6px;
          }
          
          .featured-top-badges {
            top: 4px;
            left: 4px;
            gap: 4px;
          }
          
          .live-badge-featured {
            padding: 2px 4px;
            font-size: 0.4rem;
          }
          
          .live-pulse {
            width: 4px;
            height: 4px;
          }
          
          .duration-badge {
            padding: 2px 4px;
            font-size: 0.4rem;
          }
          
          .featured-bottom {
            padding: 8px;
            gap: 4px;
          }
          
          .featured-info-overlay {
            gap: 8px;
          }
          
          .featured-avatar {
            width: 28px;
            height: 28px;
          }
          
          .avatar-letter {
            font-size: 0.8rem;
          }
          
          .featured-title {
            font-size: 0.7rem;
            margin-bottom: 2px;
          }
          
          .featured-meta {
            font-size: 0.5rem;
            gap: 3px;
          }
          
          .play-circle {
            width: 36px;
            height: 36px;
            font-size: 0.8rem;
          }
          
          /* Recommended small mobile */
          .recommended-section {
            margin-bottom: 10px;
          }
          
          .recommended-scroll {
            gap: 6px;
          }
          
          .recommended-card {
            width: 130px;
            border-radius: 5px;
          }
          
          .rec-live {
            top: 4px;
            left: 4px;
            padding: 1px 3px;
            font-size: 0.4rem;
          }
          
          .rec-viewers {
            bottom: 4px;
            right: 4px;
            padding: 1px 3px;
            font-size: 0.4rem;
          }
          
          .rec-info {
            padding: 5px 6px;
          }
          
          .rec-info h4 {
            font-size: 0.55rem;
          }
          
          .rec-info p {
            font-size: 0.45rem;
          }
          
          /* Demo notice small */
          .demo-notice {
            margin: 6px 8px;
            padding: 6px;
          }
          
          .demo-icon {
            width: 22px;
            height: 22px;
            font-size: 0.65rem;
          }
          
          .demo-text strong {
            font-size: 0.6rem;
          }
          
          .demo-text span {
            font-size: 0.5rem;
          }
          
          .demo-cta {
            padding: 4px 8px;
            font-size: 0.55rem;
          }
          
          /* Bottom nav smallest */
          .mobile-bottom-nav {
            height: 54px;
          }
          
          .nav-item {
            font-size: 0.5rem;
            min-width: 44px;
            padding: 4px 6px;
            gap: 2px;
          }
          
          .nav-item i {
            font-size: 0.9rem;
          }
          
          .nav-item .go-live-btn {
            width: 38px;
            height: 38px;
            margin-top: -14px;
          }
          
          .nav-item .go-live-btn i {
            font-size: 0.85rem;
          }
          
          .nav-item .live-indicator {
            width: 5px;
            height: 5px;
            top: 4px;
            right: 8px;
          }
        }
        
        /* === RESPONSIVE - VERY SMALL (320px and below) === */
        @media (max-width: 360px) {
          .page-header {
            padding: 5px 8px;
          }
          
          .header-title h1 {
            font-size: 0.75rem;
          }
          
          .stat {
            font-size: 0.5rem;
          }
          
          .filter-chip {
            padding: 3px 6px;
            font-size: 0.55rem;
          }
          
          .content-area {
            padding: 5px 6px 60px;
          }
          
          .streams-grid {
            gap: 4px;
          }
          
          .card-info {
            padding: 4px;
            gap: 4px;
          }
          
          .card-avatar img,
          .card-avatar .avatar-placeholder {
            width: 18px;
            height: 18px;
          }
          
          .card-title {
            font-size: 0.52rem;
          }
          
          .card-streamer {
            font-size: 0.42rem;
          }
          
          .mobile-bottom-nav {
            height: 50px;
          }
          
          .nav-item {
            font-size: 0.45rem;
            min-width: 40px;
          }
          
          .nav-item i {
            font-size: 0.8rem;
          }
          
          .nav-item .go-live-btn {
            width: 34px;
            height: 34px;
            margin-top: -12px;
          }
        }
      `}</style>
    </div>
  );
};

export default StreamsPage;
